import { useEffect, useState } from "react";
import api from "@/lib/api";
import "./RevenueCalculator.css";

const SYMBOLS = { USD: "$", GBP: "£", EUR: "€", NGN: "₦", CAD: "CA$", AUD: "A$", INR: "₹", ZAR: "R" };
const FALLBACK_RATES = { GBP: 1, USD: 1.27, EUR: 1.17, NGN: 1950, CAD: 1.72, AUD: 1.94, INR: 108, ZAR: 23.2 };
const CURRENCIES = [
  ["USD", "US Dollar"], ["GBP", "British Pound"], ["EUR", "Euro"], ["NGN", "Nigerian Naira"],
  ["CAD", "Canadian Dollar"], ["AUD", "Australian Dollar"], ["INR", "Indian Rupee"], ["ZAR", "South African Rand"],
];
const RECRUIT_PRESETS = [5, 10, 25, 50, 100, 250];
const DEFAULT_CFG = {
  rates: FALLBACK_RATES, rate_date: "", is_live: false, second_layer_avg: 3,
  commission_rate: 0.20, prices: { lite: { monthly: 10, annual: 99 }, pro: { monthly: 15, annual: 149 } },
};

export default function RevenueCalculator({ defaultDirect = 10 }) {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [rateStatus, setRateStatus] = useState("loading live rates…");
  const [direct, setDirect] = useState(String(defaultDirect || 10));
  const [pct, setPct] = useState(20);
  const [tier, setTier] = useState("lite");
  const [billing, setBilling] = useState("monthly");
  const [duration, setDuration] = useState(6);
  const [currency, setCurrency] = useState("GBP");
  const [customRate, setCustomRate] = useState(null);
  const [rateInput, setRateInput] = useState("1");

  useEffect(() => {
    let alive = true;
    api.get("/affiliates/calculator-config")
      .then(({ data }) => {
        if (!alive) return;
        const rates = { ...FALLBACK_RATES, ...(data.rates || {}) };
        setCfg({ ...DEFAULT_CFG, ...data, rates });
        setRateStatus(data.is_live ? "live rates loaded" : "using approximate rates (live feed unavailable)");
      })
      .catch(() => { if (alive) setRateStatus("using approximate rates (live feed unavailable)"); });
    return () => { alive = false; };
  }, []);

  const liveRate = cfg.rates[currency] ?? 1;
  useEffect(() => {
    if (customRate === null) setRateInput(String(cfg.rates[currency] ?? 1));
  }, [currency, cfg, customRate]);

  const RATE = cfg.commission_rate ?? 0.20;
  const SLA = cfg.second_layer_avg ?? 3;
  const activeRate = customRate !== null ? customRate : liveRate;
  const usingCustom = customRate !== null;

  const fmt = (usd) => {
    const symbol = SYMBOLS[currency] || currency + " ";
    return symbol + (usd * activeRate).toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const D = Math.max(1, parseInt(direct) || 1);
  const price = cfg.prices[tier][billing];
  const perRecruit = billing === "monthly" ? price * RATE * duration : price * RATE;
  const tier2Recruits = Math.round(D * (pct / 100) * SLA);
  const t1Total = D * perRecruit;
  const t2Total = tier2Recruits * perRecruit;
  const total = t1Total + t2Total;

  const onCurrency = (v) => { setCurrency(v); setCustomRate(null); };
  const onRateInput = (v) => {
    setRateInput(v);
    const n = parseFloat(v);
    if (!isNaN(n) && n > 0) setCustomRate(n);
  };
  const resetRate = () => { setCustomRate(null); setRateInput(String(cfg.rates[currency] ?? 1)); };

  const rateSource = usingCustom
    ? "Using a manually entered rate instead of the live rate."
    : cfg.is_live
      ? `Live rate as of ${cfg.rate_date}, for reference only.`
      : "Approximate reference rate (live feed unavailable) — edit it above if you have a more accurate figure.";

  const prices = cfg.prices;

  return (
    <div className="rev-calc" data-testid="revenue-calculator">
      <div className="card">
        <h1>Estimate your affiliate earnings</h1>
        <p className="sub">See what your referrals could be worth across both commission layers.</p>

        <div className="row">
          <label>Direct recruits per month</label>
          <input type="number" min="1" step="1" value={direct} list="rc-recruit-presets"
            onChange={(e) => setDirect(e.target.value)} data-testid="rev-calc-direct" />
          <datalist id="rc-recruit-presets">
            {RECRUIT_PRESETS.map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>

        <div className="row">
          <label>% of direct recruits who go on to refer others <span className="val" data-testid="rev-calc-pct-val">{pct}%</span></label>
          <input type="range" min="0" max="100" value={pct} onChange={(e) => setPct(parseInt(e.target.value))} data-testid="rev-calc-pct" />
        </div>

        <div className="assumption" data-testid="rev-calc-assumption">
          Each converting recruit brings in an average of {SLA} second-layer users. (Estimate — set centrally, updated as real data comes in.)
        </div>

        <div className="row">
          <label>Plan type</label>
          <div className="toggle-group">
            <div className={`toggle ${tier === "lite" ? "active" : ""}`} onClick={() => setTier("lite")} data-testid="rev-calc-tier-lite">Lite (${prices.lite.monthly}/mo · ${prices.lite.annual}/yr)</div>
            <div className={`toggle ${tier === "pro" ? "active" : ""}`} onClick={() => setTier("pro")} data-testid="rev-calc-tier-pro">Pro (${prices.pro.monthly}/mo · ${prices.pro.annual}/yr)</div>
          </div>
        </div>

        <div className="row">
          <label>Billing</label>
          <div className="toggle-group">
            <div className={`toggle ${billing === "monthly" ? "active" : ""}`} onClick={() => setBilling("monthly")} data-testid="rev-calc-billing-monthly">Monthly (recurring commission)</div>
            <div className={`toggle ${billing === "annual" ? "active" : ""}`} onClick={() => setBilling("annual")} data-testid="rev-calc-billing-annual">Annual (one-time commission)</div>
          </div>
        </div>

        {billing === "monthly" && (
          <div className="row" data-testid="rev-calc-duration-row">
            <label>Average months a recruit stays subscribed <span className="val" data-testid="rev-calc-dur-val">{duration}</span></label>
            <input type="range" min="1" max="6" value={duration} onChange={(e) => setDuration(parseInt(e.target.value))} data-testid="rev-calc-duration" />
            <div className="duration-hint">Monthly commission pays out for as long as a referral stays subscribed, up to a 6-month cap per referral. If your recruits typically churn sooner, lower this for a more realistic estimate.</div>
          </div>
        )}

        <div className="currency-block">
          <div className="currency-head">
            <span>Show earnings in</span>
            <span className="val" style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 400, fontSize: "10.5px" }} data-testid="rev-calc-rate-status">{rateStatus}</span>
          </div>
          <select value={currency} onChange={(e) => onCurrency(e.target.value)} style={{ marginTop: "8px" }} data-testid="rev-calc-currency">
            {CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
          </select>
          <div className="currency-grid">
            <div>
              <span className="field-label">Exchange rate used (1 USD =)</span>
              <input type="number" step="0.0001" min="0" value={rateInput} onChange={(e) => onRateInput(e.target.value)} data-testid="rev-calc-rate-input" />
            </div>
            <div style={{ alignSelf: "end" }}>
              <span className="reset-link" onClick={resetRate} data-testid="rev-calc-reset">Reset to live rate</span>
            </div>
          </div>
          <div className="rate-line" data-testid="rev-calc-rate-line">
            1 USD = {activeRate.toFixed(4)} {currency}
            {usingCustom && <span className="custom-rate-flag">YOUR RATE</span>}
          </div>
          <div className="rate-source">{rateSource}</div>
        </div>

        <div className="divider" />

        <div className="per-recruit-callout" data-testid="rev-calc-per-recruit">
          <div className="big">{fmt(perRecruit)}</div>
          <div className="lbl">LIFETIME VALUE PER RECRUIT — applies equally to Tier 1 and Tier 2</div>
        </div>

        <div className="results">
          <div className="result-box">
            <div className="result-label">Tier 1 — direct</div>
            <div className="result-value" data-testid="rev-calc-t1">{fmt(t1Total)}</div>
            <div className="result-note">{D} direct recruits × {fmt(perRecruit)} lifetime value</div>
          </div>
          <div className="result-box">
            <div className="result-label">Tier 2 — second layer</div>
            <div className="result-value" data-testid="rev-calc-t2">{fmt(t2Total)}</div>
            <div className="result-note">{tier2Recruits} second-layer recruits ({D} × {pct}% × {SLA}) × {fmt(perRecruit)} lifetime value</div>
          </div>
          <div className="result-box total">
            <div className="result-label">Total value from this month's recruits</div>
            <div className="result-value" data-testid="rev-calc-total">{fmt(total)}</div>
            <div className="result-note">{billing === "monthly" ? `Lifetime value at an average ${duration}-month subscription` : "One-time, from this month's referrals"}</div>
          </div>
        </div>

        <p className="disclaimer"><b>This is an estimate, not a payout amount.</b> Figures are based on what you enter here, an assumed average second-layer referral rate, and either a live or manually entered exchange rate (shown above). Commissions are calculated in GBP and paid via Stripe Connect (or Paystack for Nigeria) — the amount you actually receive in your local currency depends on the payout provider's own conversion rate and any fees applied at the time of payout, which may differ from what's shown here.</p>
      </div>
    </div>
  );
}
